import { z } from 'zod';
import { ReportBrandingIds, ReportIds } from '../enums';

export const reportDocumentColumnFormatSchema = z.enum(['text', 'number', 'currency', 'integer', 'decimal1']);
export type ReportDocumentColumnFormat = z.infer<typeof reportDocumentColumnFormatSchema>;

export const reportDocumentColumnAlignSchema = z.enum(['left', 'right', 'center']);
export type ReportDocumentColumnAlign = z.infer<typeof reportDocumentColumnAlignSchema>;

export const reportDocumentLogoSchema = z.object({
  type: z.enum(['url', 'dataUri']),
  value: z.string().min(1),
});

export type ReportDocumentLogo = z.infer<typeof reportDocumentLogoSchema>;

export const reportBrandingColorSchemeSchema = z.object({
  brandColor: z.string(),
  bulletinTitleColor: z.string(),
  reportTitleColor: z.string(),
  text: z.string(),
});

export type ReportBrandingColorScheme = z.infer<typeof reportBrandingColorSchemeSchema>;

const reportDocumentBrandingSchema = z.object({
  id: z.union([z.nativeEnum(ReportBrandingIds), z.number().int().nullable()]),
  logo: reportDocumentLogoSchema.optional(),
  colorScheme: reportBrandingColorSchemeSchema,
});

export type ReportDocumentBranding = z.infer<typeof reportDocumentBrandingSchema>;

export const reportDocumentMetaSchema = z.object({
  reportId: z.nativeEnum(ReportIds),
  title: z.string(),
  subtitle: z.string().optional(),
  generatedAtIso: z.string().optional(),
  periodLabel: z.string().optional(),
  locale: z.string().optional(),
  currency: z.string().optional(),
  timeZone: z.string().optional(),
  branding: reportDocumentBrandingSchema,
});

export type ReportDocumentMeta = z.infer<typeof reportDocumentMetaSchema>;

export const reportDocumentLayoutSchema = z.object({
  pageSize: z.string().optional(),
  orientation: z.enum(['portrait', 'landscape']).optional(),
  margins: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
  repeatTableHeader: z.boolean().optional(),
  showPageNumbers: z.boolean().optional(),
  showGeneratedAt: z.boolean().optional(),
  continuedLabel: z.string().optional(),
});

export type ReportDocumentLayout = z.infer<typeof reportDocumentLayoutSchema>;

export const reportDocumentColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  format: reportDocumentColumnFormatSchema.optional(),
  align: reportDocumentColumnAlignSchema.optional(),
  width: z.union([z.string(), z.number()]).optional(),
  sumTotal: z.boolean().optional(),
});

export type ReportDocumentColumn = z.infer<typeof reportDocumentColumnSchema>;

export const reportDocumentSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  columns: z.array(reportDocumentColumnSchema),
  rows: z.array(z.record(z.union([z.string(), z.number()]))),
  totals: z.record(z.number()).optional(),
  showTotals: z.boolean().optional(),
  totalLabel: z.string().optional(),
});

export type ReportDocumentSection = z.infer<typeof reportDocumentSectionSchema>;

export const reportDocumentSchema = z.object({
  meta: reportDocumentMetaSchema,
  layout: reportDocumentLayoutSchema.optional(),
  sections: z.array(reportDocumentSectionSchema),
});

export type ReportDocumentDto = z.infer<typeof reportDocumentSchema>;
